import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import VendorDashboard from './pages/VendorDashboard';
import PayrollPage from './pages/PayrollPage';
import RevenueBuildPage from './pages/RevenueBuildPage';
import DAModulePage from './pages/DAModulePage';
import IncomeStatementPage from './pages/IncomeStatementPage';
import BalanceSheetPage from './pages/BalanceSheetPage';
import CashFlowPage from './pages/CashFlowPage';
import DeferredRevenuePage from './pages/DeferredRevenuePage';
import KPIDashboardPage from './pages/KPIDashboardPage';
import ScenariosPage from './pages/ScenariosPage';
import FundraisingPage from './pages/FundraisingPage';
import AdminPage from './pages/AdminPage';
import { isSuperAdmin, canEditModel } from './utils/audience';
import { EyeIcon } from './components/PasswordInput';
import { getCompanies, onMustChangePassword } from './api';
import ForcedPasswordScreen from './components/ForcedPasswordScreen';
import ChangePasswordDialog from './components/ChangePasswordDialog';
import ModelProvider from './ModelProvider';
import ErrorBoundary from './components/ErrorBoundary';

// Placeholder pages for modules we haven't wired up yet
const Placeholder = ({ title }) => (
    <div style={{ padding: 32 }}>
        <h2 style={{ color: '#1A1C2E', margin: 0 }}>{title}</h2>
        <p style={{ color: '#666', marginTop: 8 }}>Coming soon — will be connected to database.</p>
    </div>
);

const NAV_ITEMS = [
    { path: '/kpi', label: 'KPI Dashboard', icon: '🎯' },
    { path: '/is', label: 'Income Statement', icon: '📊' },
    { path: '/bs', label: 'Balance Sheet', icon: '⚖️' },
    { path: '/cfs', label: 'Cash Flow', icon: '💰' },
    { path: '/revenue', label: 'Revenue Build', icon: '📈' },
    { path: '/vendors', label: 'Vendor Dashboard', icon: '📋' },
    { path: '/payroll', label: 'Payroll', icon: '👥' },
    { path: '/assets', label: 'Assets (FA/IA)', icon: '🏗️' },
    { path: '/defrev', label: 'Deferred Revenue', icon: '📅' },
    { path: '/scenarios', label: 'Scenarios', icon: '🔮' },
    { path: '/fundraising', label: 'Fundraising', icon: '💵' },
    { path: '/admin', label: 'Admin', icon: '⚙️' },
];

function AppShell({ user, onLogout, onUserChanged }) {
    const [companies, setCompanies] = useState([]);
    // W-053 step 3a: Change password, beside Sign out (every page, every role)
    const [changingPassword, setChangingPassword] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    // Batch 1b (2026-11-03, L3619): the company whose onboarding wizard is open on /admin (AdminPage reports it), or null. Display only:
    // the top bar names it while a wizard is open, because the wizard does not act on the switcher's company.
    const [wizardCompany, setWizardCompany] = useState(null);
    // 9d: distinguishes "still fetching" from "fetched, and none are live".
    // Without it the no-live-company case is indistinguishable from a slow network,
    // which is exactly how it came to render a permanent "Loading..." forever.
    const [companiesLoaded, setCompaniesLoaded] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    /* W-025: every routed page sits inside its OWN ErrorBoundary, so a page that throws while drawing shows a
       readable message in the page slot while the sidebar, the company switcher and the top bar -- siblings of
       <Routes>, not ancestors of a page -- stay usable. The page name comes from NAV_ITEMS (one list); resetKey
       is the route, so moving to another page clears the message. */
    const pageLabel = path => (NAV_ITEMS.find(i => i.path === path) || {}).label || path;
    const tenantName = selectedCompany ? selectedCompany.name : null;
    const guard = (path, el, tenant = tenantName) => (
        <ErrorBoundary page={pageLabel(path)} tenant={tenant} resetKey={location.pathname}>{el}</ErrorBoundary>
    );

    useEffect(() => {
        loadCompanies();
    }, []);

    // Step-9 "Configure on" links (AdminPage): select the wizard's tenant in the NAV, then
    // navigate to the owning page. ★ THIS IS A WIRE, NOT A FEATURE — setSelectedCompany +
    // the existing `key={selectedCompany.id}` on ModelProvider already does the whole job
    // (remount → refetch → recompute for the tenant); the wizard just could not reach the
    // setter (AdminPage gets no props and sits outside ModelProvider). Without this wire the
    // link landed on whatever company the nav happened to hold — the operator configured
    // Semitek thinking it was AE Model. ★ GUARD: the SWITCHER'S OWN predicate — the link
    // must not select what the switcher cannot (one predicate, two consumers).
    /* W-027: THE ONE SELECTABILITY PREDICATE -- the switcher's filter and configureCompany both call it
       (it was spelled twice). Live, or onboarded for the super admin. */
    const isSelectable = c => !!c && (c.is_engine_ready || (isSuperAdmin(user) && c.is_onboarded));

    /* W-027: THE ONE FETCH OF THE COMPANY LIST. It used to run once, at login, so a company CREATED or
       ONBOARDED or made LIVE later in the same session was missing or stale here, and every Step-9 link
       for it was refused ("not selectable until onboarding is complete") until a browser reload. Now
       the login load, configureCompany's retry, the switcher's focus and AdminPage (after a create, the
       Step-8 import and go-live -- via onCompaniesChanged) all call this one function. It updates the
       LIST only: the selected company and the page the operator is on are untouched. */
    const refreshCompanies = async () => {
        const res = await getCompanies();
        setCompanies(res.data);
        return res.data;
    };

    /* W-027: a miss is checked against a FRESH list before it is refused -- the stale-list case is the
       one a new tenant hits. Returns a promise of true / false (AdminPage awaits it). */
    const configureCompany = async (companyId, path) => {
        let co = companies.find(c => c.id === companyId);
        if (!isSelectable(co)) {
            try { co = (await refreshCompanies()).find(c => c.id === companyId); } catch (e) { co = null; }
        }
        if (!isSelectable(co)) return false;
        setSelectedCompany(co);
        navigate(path);
        return true;
    };

    const loadCompanies = async () => {
        try {
            const res = { data: await refreshCompanies() };
            // Auto-select company: only from engine-ready companies.
            // is_onboarded=true is not enough — the live engines are still
            // hardcoded to specific charts of accounts, so pre-refactor we
            // only surface companies that have been explicitly validated.
            const live = res.data.filter(c => c.is_engine_ready);
            if (live.length > 0) {
                // ★ 2026-08-12: was `live.find(c => c.id === 1) || live[0]` — a hardcoded
                // preference for cid=1 (Semitek), which has been DELETED. It was the last
                // executable `1` meaning Semitek anywhere in the running app.
                //
                // ⚠ THE OBVIOUS REPLACEMENT — plain `live[0]` — IS WRONG HERE, and only a
                // measurement showed it: /api/companies is `ORDER BY name` (server.js:125),
                // and "A/B Harness Tenant" sorts BEFORE "AE Model" ('/' 0x2F < 'E' 0x45).
                // So live[0] would land every super_admin login on the FROZEN A/B HARNESS —
                // the one tenant whose entire value is that nobody edits it, and whose
                // golden was promoted the same day. A default that puts the operator inside
                // the instrument is a bad default even though nothing "breaks".
                //
                // ⇒ LOWEST id = the OLDEST live tenant. Deterministic, no tenant-id literal
                // (which is the thing being removed), and stable as tenants come and go: a
                // newly-created tenant always gets a higher id and can never hijack the
                // default the way a rename can under name-ordering. Today that is cid=3
                // "AE Model", the oracle.
                // A scoped user is unaffected — they still get THEIR company.
                const oldestLive = [...live].sort((a, b) => a.id - b.id)[0];
                const match = user.company_id
                    ? live.find(c => c.id === user.company_id)
                    : oldestLive;
                setSelectedCompany(match || oldestLive);
            }
            setCompaniesLoaded(true);
        } catch (err) {
            console.error('Failed to load companies:', err);
            setCompaniesLoaded(true);   // a failed fetch is still a resolved state, not a spinner
        }
    };

    const sidebarWidth = sidebarCollapsed ? 56 : 220;

    return (
        <div style={{ display: 'flex', height: '100vh', background: '#f0f2f5' }}>
            {/* Sidebar */}
            <div style={{
                width: sidebarWidth,
                background: '#1A1C2E',
                color: '#fff',
                transition: 'width 0.2s',
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0
            }}>
                {/* Header */}
                <div style={{
                    padding: sidebarCollapsed ? '16px 8px' : '16px 16px',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    {!sidebarCollapsed && (
                        <span style={{ fontWeight: 700, fontSize: 15 }}>Finledom</span>
                    )}
                    <button
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        style={{
                            background: 'none', border: 'none', color: '#aaa',
                            cursor: 'pointer', fontSize: 16, padding: 4
                        }}
                    >
                        {sidebarCollapsed ? '→' : '←'}
                    </button>
                </div>

                {/* Company Selector — super_admin additionally sees ONBOARDED-but-not-ready tenants,
                    tagged "(setup)". ★ 9a-2: this is NOT a convenience — it FORMALIZES the ad-hoc
                    tactical flip (§11 "the AE Model workaround") and BREAKS THE CIRCULARITY that
                    made the go-live gate dishonest: is_engine_ready gated this switcher, so a
                    not-ready tenant's production pages (where module settings are configured) were
                    UNREACHABLE — you had to FLIP the go-live flag to reach the forms that EARN the
                    flip. Keys on is_onboarded (Step-8 complete: data is in, the model computes) —
                    never on pre-Step-8 tenants (the model may not compute; that is why the Step-4b
                    bridge exists). Non-super_admin users are unchanged (engine-ready only). */}
                {(() => {
                    const selectable = companies.filter(isSelectable);
                    if (!isSuperAdmin(user) || selectable.length <= 1 || sidebarCollapsed) return null;
                    return (
                        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                            <select
                                value={selectedCompany?.id || ''}
                                /* W-027: re-read the list when the operator opens the switcher, so a company
                                   made live elsewhere since the last read is offered. A failed read keeps the list. */
                                onFocus={() => { refreshCompanies().catch(() => {}); }}
                                onChange={e => {
                                    const co = companies.find(c => c.id === parseInt(e.target.value));
                                    setSelectedCompany(co);
                                }}
                                style={{
                                    width: '100%', padding: '6px 8px', borderRadius: 4,
                                    border: '1px solid #444', background: '#2a2d42',
                                    color: '#fff', fontSize: 13
                                }}
                            >
                                {selectable.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}{c.is_engine_ready ? '' : ' (setup)'}</option>
                                ))}
                            </select>
                        </div>
                    );
                })()}

                {/* Nav Links */}
                <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
                    {NAV_ITEMS.map(item => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            style={({ isActive }) => ({
                                display: 'flex',
                                alignItems: 'center',
                                padding: sidebarCollapsed ? '10px 16px' : '10px 16px',
                                color: isActive ? '#fff' : '#aaa',
                                background: isActive ? 'rgba(42,109,176,0.3)' : 'transparent',
                                borderLeft: isActive ? '3px solid #2A6DB0' : '3px solid transparent',
                                textDecoration: 'none',
                                fontSize: 13,
                                transition: 'all 0.15s',
                                gap: 10
                            })}
                        >
                            <span style={{ fontSize: 16 }}>{item.icon}</span>
                            {!sidebarCollapsed && <span>{item.label}</span>}
                        </NavLink>
                    ))}
                </nav>

                {/* User Footer */}
                <div style={{
                    padding: sidebarCollapsed ? '12px 8px' : '12px 16px',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    fontSize: 12
                }}>
                    {/* W-053 3b.2: the email #C9CBD6 (10.4:1 on #1A1C2E; was #aaa 7.2:1) and the role line #A3A6B8 (7.0:1; was #666
                        2.9:1, below AA); "View only" is an amber pill (index.css .fl-vo-pill), and the two actions are real
                        buttons (.fl-side-btn) -- full width, 32px, a visible outline, hover + keyboard focus. */}
                    {!sidebarCollapsed && (
                        <>
                            <div style={{ color: '#C9CBD6', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {user.email}
                            </div>
                            <div style={{ color: '#A3A6B8', marginBottom: 10, fontSize: 11 }}>
                                {/* W-053 3b: a login that may not change the model reads "View only" (was its raw role). */}
                                {isSuperAdmin(user) ? 'Super Admin' : canEditModel(user) ? user.role
                                    : <span data-view-only-badge="" className="fl-vo-pill"><EyeIcon size={13} />View only</span>}
                            </div>
                        </>
                    )}
                    {sidebarCollapsed && !canEditModel(user) && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                            <div data-view-only-badge="" title="View only" aria-label="View only" role="img" className="fl-vo-pill fl-vo-pill--icon">
                                <EyeIcon size={16} />
                            </div>
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: 6, flexDirection: 'column' }}>
                    <button
                        type="button"
                        onClick={onLogout}
                        title={sidebarCollapsed ? 'Sign Out' : undefined}
                        className="fl-side-btn"
                    >
                        {sidebarCollapsed ? '⏻' : 'Sign Out'}
                    </button>
                    <button
                        type="button"
                        onClick={() => setChangingPassword(true)}
                        title="Change password"
                        className="fl-side-btn"
                    >
                        {sidebarCollapsed ? '🔑' : 'Change password'}
                    </button>
                    </div>
                </div>
                {changingPassword && (
                    <ChangePasswordDialog user={user} onClose={() => setChangingPassword(false)} onChanged={onUserChanged} />
                )}
            </div>

            {/* Main Content */}
            {/* The main column is a bounded flex column: the top bar keeps its height and the page
                area below it is the scroller, so a full-height page fills exactly the space under
                the bar and the window itself never scrolls (diffs/table-scroll-build.txt). */}
            <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {/* Top Bar */}
                <div style={{
                    flexShrink: 0,
                    background: '#fff',
                    borderBottom: '1px solid #e0e0e0',
                    padding: '12px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    {/* Batch 1b (L3619): while an onboarding wizard is open on /admin, the bar names THAT company */}
                    {(() => { const wiz = location.pathname === '/admin' ? wizardCompany : null; const shown = wiz || selectedCompany; return (<>
                    <div data-topbar-company="" style={{ fontWeight: 600, fontSize: 16, color: '#1A1C2E' }}>
                        {wiz ? 'Onboarding: ' + wiz.name : (selectedCompany?.name || 'No Company Selected')}
                    </div>
                    <div style={{ fontSize: 12, color: '#999' }}>
                        FY End: {shown?.fiscal_year_end || '--'}
                    </div>
                    </>); })()}
                </div>

                {/* Page Content */}
                <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                    {/* ★ 9d: /admin renders WITHOUT a selected company — the BOOTSTRAP
                        DEADLOCK (§12 hygiene). All routes used to sit behind selectedCompany,
                        so a fresh deployment with zero engine-ready tenants could never reach
                        /admin to onboard the first one. AdminPage is self-sufficient (it fetches
                        its own companies and owns selectedCompanyId), so it needs no gate. */}
                    <Routes>
                        <Route path="/admin" element={guard('/admin', <AdminPage onConfigureCompany={configureCompany} onCompaniesChanged={refreshCompanies} user={user} navCompany={selectedCompany} onUserChanged={onUserChanged} onWizardCompany={setWizardCompany} />, null)} />
                        <Route path="*" element={selectedCompany ? (
                            <Routes>
                            <Route path="*" element={
                                <ErrorBoundary page={pageLabel(location.pathname)} tenant={tenantName} resetKey={location.pathname}>
                                <ModelProvider companyId={selectedCompany.id} key={selectedCompany.id} companyName={tenantName} pageLabel={pageLabel(location.pathname)} canEditModel={canEditModel(user)} superAdmin={isSuperAdmin(user)}>
                                <Routes>
                                    <Route path="/" element={<Navigate to="/vendors" replace />} />
                                    <Route path="/vendors" element={guard('/vendors', <VendorDashboard companyId={selectedCompany.id} />)} />
                                    <Route path="/payroll" element={guard('/payroll', <PayrollPage companyId={selectedCompany.id} />)} />
                                    <Route path="/revenue" element={guard('/revenue', <RevenueBuildPage companyId={selectedCompany.id} />)} />
                                    <Route path="/assets" element={guard('/assets', <DAModulePage companyId={selectedCompany.id} />)} />
                                    <Route path="/is" element={guard('/is', <IncomeStatementPage companyId={selectedCompany.id} />)} />
                                    <Route path="/bs" element={guard('/bs', <BalanceSheetPage companyId={selectedCompany.id} />)} />
                                    <Route path="/cfs" element={guard('/cfs', <CashFlowPage companyId={selectedCompany.id} />)} />
                                    <Route path="/defrev" element={guard('/defrev', <DeferredRevenuePage companyId={selectedCompany.id} />)} />
                                    <Route path="/fundraising" element={guard('/fundraising', <FundraisingPage companyId={selectedCompany.id} />)} />
                                    <Route path="/scenarios" element={guard('/scenarios', <ScenariosPage companyId={selectedCompany.id} />)} />
                                    <Route path="/kpi" element={guard('/kpi', <KPIDashboardPage companyId={selectedCompany.id} />)} />
                                </Routes>
                                </ModelProvider>
                                </ErrorBoundary>
                            } />
                            </Routes>
                        ) : !companiesLoaded ? (
                            <div style={{ padding: 32, color: '#666' }}>Loading company data...</div>
                        ) : (
                            /* ★ 9d: the HONEST EMPTY STATE. This branch used to render
                               "Loading company data..." forever: loadCompanies only calls
                               setSelectedCompany when at least one company is engine-ready, so a
                               non-super_admin whose only company is not yet live got a spinner
                               that could never resolve — no error, no message, no route. A
                               fail-loud state is a NAMED HANDLED STATE, not a stuck one
                               (§12.5 rule 7). super_admin is unaffected: the switcher already
                               admits onboarded-but-not-ready tenants, so they reach this branch
                               only when NO company is live at all. */
                            <div style={{ padding: 32, fontFamily: "'DM Sans',-apple-system,sans-serif", color: '#1A1C2E', maxWidth: 620 }}>
                                <h2 style={{ fontSize: 18, margin: '0 0 8px' }}>This company is not yet live</h2>
                                <p style={{ fontSize: 14, color: '#666', margin: '0 0 8px' }}>
                                    Your company has been set up but has not been switched on yet, so its
                                    dashboards are not available. Nothing is wrong with your data.
                                </p>
                                <p style={{ fontSize: 13, color: '#888', margin: 0 }}>
                                    {isSuperAdmin(user)
                                        ? 'No company is engine-ready. Open Admin to finish onboarding one.'
                                        : 'Please contact your administrator — they can complete setup and switch it on.'}
                                </p>
                            </div>
                        )} />
                    </Routes>
                </div>
            </div>
        </div>
    );
}

export default function App() {
    const [user, setUser] = useState(() => {
        const saved = localStorage.getItem('user');
        return saved ? JSON.parse(saved) : null;
    });

    const handleLogin = (userData) => {
        setUser(userData);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
    };

    /* W-053 step 3a: the signed-in user changed under us -- a password change (its new token + user are already stored by
       api.js) or a change of YOUR OWN role (re-read via GET /api/auth/me). Kept in the login's shape, stored, and shown. */
    const handleUserChanged = (u) => {
        if (!u) return;
        const next = { id: u.id, email: u.email, role: u.role, company_id: u.company_id, must_change_password: u.must_change_password === true };
        localStorage.setItem('user', JSON.stringify(next));
        setUser(next);
    };

    /* W-053 step 3a: THE FORCED-CHANGE SCREEN. api.js's response interceptor is the ONE place that recognises a 403
       MUST_CHANGE_PASSWORD (from any route); it calls this listener, which flags the user so the screen below replaces the
       app until a new password is set. A login that returns must_change_password lands on the same screen. */
    useEffect(() => onMustChangePassword(() => {
        setUser(u => (u && !u.must_change_password ? { ...u, must_change_password: true } : u));
    }), []);

    return (
        /* W-025: the last resort -- a fault in the shell itself (sidebar, top bar, login) is caught here. */
        <ErrorBoundary page="Finledom">
        <BrowserRouter>
            {user && user.must_change_password ? (
                <ForcedPasswordScreen user={user} onDone={handleUserChanged} onLogout={handleLogout} />
            ) : user ? (
                <AppShell user={user} onLogout={handleLogout} onUserChanged={handleUserChanged} />
            ) : (
                <Routes>
                    <Route path="*" element={<LoginPage onLogin={handleLogin} />} />
                </Routes>
            )}
        </BrowserRouter>
        </ErrorBoundary>
    );
}
